import { useEffect } from 'react';
import { Box, Spinner } from '@nimbus-ds/components';
import { useParams } from 'react-router-dom';
import { useDesktopMenuContext } from 'App/components/Menu/DesktopMenuProvider';
import { useNavegate } from 'App/hooks';
import { HeaderContent, HeaderTop, IonPageStratus } from 'commons/components';
import { configurationsRoutes } from 'domains/Configurations';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import {
  PendingOrderCard,
  ErrorOrderCard,
  SuccessOrderCard,
  UnderHelpLink,
} from './components';
import { useOrderPolling } from './hooks';
import { useOrderState } from './utils';
import {
  useGetDomainOrderQuery,
  useAcknowledgeDomainOrderMutation,
} from '../../domainsApi';

interface DomainsOrderDetailsPageParams {
  payOrderId: string;
}

export function DomainsOrderDetailsPage() {
  const t = useTranslationConfigurations('domains.orderDetails');
  const { payOrderId } = useParams<DomainsOrderDetailsPageParams>();
  const { goTo } = useNavegate();
  const { hideMenu, showMenu } = useDesktopMenuContext();

  const {
    data: orderData,
    isLoading,
    isError,
  } = useGetDomainOrderQuery(payOrderId ?? '', {
    skip: !payOrderId,
  });

  const [acknowledgeDomainOrder] = useAcknowledgeDomainOrderMutation();

  const orderState = useOrderState(orderData);

  useOrderPolling({
    payOrderId: payOrderId ?? '',
    orderState,
    enabled: !isLoading && !!payOrderId,
  });

  const handleGoToDomains = async () => {
    if (payOrderId && orderState === 'error') {
      await acknowledgeDomainOrder(payOrderId);
    }
    goTo(configurationsRoutes.domains);
  };

  useEffect(() => {
    if (isError || orderState === 'redirect') {
      goTo(configurationsRoutes.domains);
    }
  }, [isError, orderState, goTo]);

  useEffect(() => {
    if (orderState === 'success') {
      showMenu();
    } else {
      hideMenu();
    }

    return () => {
      showMenu();
    };
  }, [orderState, hideMenu, showMenu]);

  const getPageTitle = (): string => {
    if (!orderData) return '';

    switch (orderState) {
      case 'pending':
        return `${t('pageTitlePending')} ${orderData.domainName}`;
      case 'error':
        return t('pageTitleError');
      case 'success':
        return t('pageTitleSuccess');
      default:
        return '';
    }
  };

  const shouldShowBackButton = (): boolean =>
    orderState === 'pending' || orderState === 'error';

  const backNavigation = shouldShowBackButton()
    ? {
        onClick: handleGoToDomains,
      }
    : undefined;

  if (isLoading || !payOrderId || !orderData) {
    return (
      <IonPageStratus width="medium">
        <Box padding="4" display="flex" justifyContent="center">
          <Spinner />
        </Box>
      </IonPageStratus>
    );
  }

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        backNavigation ? <HeaderTop navigation={backNavigation} /> : undefined
      }
      headerContent={<HeaderContent title={getPageTitle()} />}
    >
      {orderState === 'pending' && <PendingOrderCard order={orderData} />}
      {orderState === 'error' && <ErrorOrderCard order={orderData} />}
      {orderState === 'success' && <SuccessOrderCard order={orderData} />}
      <UnderHelpLink orderState={orderState} />
    </IonPageStratus>
  );
}
